import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/hooks/useLanguage';
import { ChevronDown } from 'lucide-react';

const HERO_IMAGE = 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&auto=format&fit=crop';

const currentIssue = () => {
  const now = new Date();
  const month = now.toLocaleString('en', { month: 'long' });
  return `Issue ${String(now.getMonth() + 1).padStart(2, '0')} \u00b7 ${month} ${now.getFullYear()}`;
};

export const HeroSection = () => {
  const { t } = useLanguage();

  const scrollToCategories = () => {
    document.getElementById('categories')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-[88vh] md:min-h-[92vh] flex items-end justify-center overflow-hidden">
      <Helmet>
        <link rel="preload" as="image" href={HERO_IMAGE} />
      </Helmet>

      {/* Ken-Burns background image */}
      <div className="absolute inset-0 overflow-hidden">
        <img
          src={HERO_IMAGE}
          alt="Mountain landscape representing alternative living"
          className="w-full h-full object-cover animate-ken-burns will-change-transform"
          width={1920}
          height={1080}
          {...({ fetchpriority: "high" } as Record<string, string>)}
          loading="eager"
          decoding="async"
        />
        {/* Top vignette + cinematic bottom gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/10 to-transparent" />
        <div className="absolute inset-0 gradient-cinematic" />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-6 md:px-8 text-center pb-24 md:pb-32">
        <p
          className="editorial-rule mx-auto mb-10 animate-fade-in"
          style={{ color: 'rgba(255,255,255,0.85)' }}
        >
          <span style={{ background: 'rgba(255,255,255,0.45)' }} />
          {currentIssue()}
        </p>

        <h1
          className="font-display text-[2.5rem] sm:text-6xl md:text-7xl lg:text-8xl xl:text-[8.5rem] font-medium text-white mb-6 sm:mb-8 animate-fade-in tracking-[-0.02em] leading-[1.02] sm:leading-[0.95] max-w-5xl mx-auto"
          style={{ textShadow: '0 2px 40px rgba(0,0,0,0.45)' }}
        >
          {t('hero.title')}
        </h1>

        <p
          className="text-sm md:text-base text-white/85 max-w-md mx-auto mb-10 md:mb-14 px-2 animate-fade-in font-light leading-relaxed tracking-wide"
          style={{ animationDelay: '0.2s', textShadow: '0 1px 15px rgba(0,0,0,0.55)' }}
        >
          {t('hero.subtitle')}
        </p>

        <button
          onClick={scrollToCategories}
          className="group inline-flex items-center gap-3 px-8 sm:px-10 py-3.5 sm:py-4 border border-white/70 text-white hover:bg-white hover:text-primary transition-all duration-700 ease-editorial animate-fade-in font-mono text-[11px] tracking-editorial uppercase"
          style={{ animationDelay: '0.4s' }}
        >
          {t('hero.cta')}
          <ChevronDown className="w-4 h-4 group-hover:translate-y-0.5 transition-transform duration-700" strokeWidth={1.5} />
        </button>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-3 animate-fade-in" style={{ animationDelay: '0.6s' }}>
          <span className="font-mono text-[9px] tracking-editorial uppercase text-white/80">Scroll</span>
          <div className="w-px h-10 bg-white/40 animate-scroll-pulse" />
        </div>
      </div>
    </section>
  );
};
